import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Eye, Compass, Zap, Shield,
  Search, FileText, CreditCard, Package,
  ChevronRight, MapPin, Activity
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { CategoryGrid } from '../components/CategoryComponents';
import { CurrencyBadge } from '../components/CurrencyDisplay';
import SEOHead from '../components/SEOHead';
import SocialFeed from '../components/SocialFeed';
import ShareButtons from '../components/ShareButtons';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Particles, MouseGradient, Reveal, AnimatedCounter } from '../components/BrandEffects';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==========================================
// LIVE TICKER
// ==========================================
function LiveTicker() {
  const { rate } = useCurrency();
  const messages = useMemo(() => [
    { icon: Activity, text: '12 suppliers online now', color: '#10B981' },
    { icon: Package, text: 'Last delivery: 47 min ago', color: '#F59E0B' },
    { icon: CreditCard, text: `1 ZAR = ${rate} MZN`, color: '#3B82F6' },
    { icon: Shield, text: '100% delivery success rate', color: '#8B5CF6' },
  ], [rate]);

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % messages.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [messages.length]);

  const msg = messages[current];

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs">
      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: msg.color }} />
      <motion.span
        key={current}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="text-slate-400"
      >
        {msg.text}
      </motion.span>
    </div>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function HomePage() {
  const { t } = useLanguage();
  const { rate } = useCurrency();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [platformStats, setPlatformStats] = useState({ products: 100, suppliers: 15, deliveries: 50, countries: 2 });

  const fetchHomeData = useCallback(async () => {
    try {
      const [catRes, prodRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories/tree`),
        axios.get(`${API_URL}/api/products?limit=8`),
        axios.get(`${API_URL}/api/stats/public`),
      ]);
      setCategories(catRes.data);
      setFeaturedProducts(prodRes.data.products || []);
      setPlatformStats(statsRes.data);
    } catch (err) {
      console.error('Homepage data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]);

  const steps = [
    { icon: Search, title: t('how_step_1_title'), description: t('how_step_1_desc'), num: '01' },
    { icon: FileText, title: t('how_step_2_title'), description: t('how_step_2_desc'), num: '02' },
    { icon: CreditCard, title: t('how_step_3_title'), description: t('how_step_3_desc'), num: '03' },
    { icon: Package, title: t('how_step_4_title'), description: t('how_step_4_desc'), num: '04' },
  ];

  const pillars = [
    { icon: Eye, title: t('value_radical_clarity'), desc: t('value_radical_clarity_desc'), accent: '#F59E0B' },
    { icon: Compass, title: t('value_end_to_end'), desc: t('value_end_to_end_desc'), accent: '#1E3A8A' },
    { icon: Zap, title: t('value_african_speed'), desc: t('value_african_speed_desc'), accent: '#10B981' },
    { icon: Shield, title: t('value_trust_infra'), desc: t('value_trust_infra_desc'), accent: '#8B5CF6' },
  ];

  const FeaturedProductCard = ({ product, index }) => {
    const name = product.name_en;
    const firstImg = product.images?.[0];
    const imgUrl = firstImg ? (typeof firstImg === 'string' ? firstImg : (firstImg.thumbnail_url || firstImg.url)) : null;
    
    return (
      <Reveal delay={index * 0.05}>
        <Link
          to={`/products/${product.slug || product.product_id}`}
          className="group bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-slate-400"
          data-testid={`featured-product-${product.product_id}`}
        >
          <div className="aspect-square overflow-hidden bg-slate-50">
            {imgUrl ? (
              <img src={imgUrl} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-10 h-10 text-slate-200" />
              </div>
            )}
          </div>
          <div className="p-3">
            <h3 className="font-medium text-slate-800 text-sm line-clamp-2 h-10 group-hover:text-[#1E3A8A] transition-colors">{name}</h3>
            <p className="text-slate-900 font-bold mt-1">R {product.price_zar?.toLocaleString()}</p>
            <p className="text-xs text-slate-400">{(product.price_mzn || product.price_zar * rate).toLocaleString()} MZN</p>
          </div>
        </Link>
      </Reveal>
    );
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "The Guy in SA",
    "url": "https://theguyinsa.co.za",
    "logo": "https://theguyinsa.co.za/logo.png",
    "description": "Cross-border procurement and logistics from Johannesburg to Maputo.",
    "areaServed": [{"@type": "Country", "name": "South Africa"}, {"@type": "Country", "name": "Mozambique"}],
    "contactPoint": [
      {"@type": "ContactPoint", "telephone": "+27783891391", "contactType": "sales", "areaServed": "ZA", "availableLanguage": "English"},
      {"@type": "ContactPoint", "telephone": "+258846943840", "contactType": "sales", "areaServed": "MZ", "availableLanguage": ["Portuguese", "English"]}
    ],
    "sameAs": ["https://www.facebook.com/theguyinsaa", "https://www.instagram.com/theguyinsa"]
  };

  return (
    <Layout>
      <SEOHead schema={orgSchema} />
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-[#0F172A]" data-testid="hero-section">
        {/* Grid bg */}
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '60px 60px'}} />
        
        {/* Animated glow */}
        <div className="hero-grid-glow" />
        
        {/* Particles */}
        <Particles />
        
        {/* Mouse gradient */}
        <MouseGradient />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[88vh] py-20 lg:py-0">
            {/* Text */}
            <div className="space-y-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6 }}
                className="flex flex-wrap items-center gap-3"
              >
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 rounded-full backdrop-blur-sm">
                  <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                  <span className="text-sm text-slate-300 tracking-wide">Johannesburg &rarr; Maputo</span>
                  <CurrencyBadge />
                </div>
                <LiveTicker />
              </motion.div>
              
              <div>
                <motion.h1 
                  initial={{ opacity: 0, y: 30 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.7, delay: 0.15 }}
                  className="text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1] font-bold tracking-tight"
                >
                  {t('hero_title')}
                </motion.h1>
                <motion.span 
                  initial={{ opacity: 0, y: 30 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.7, delay: 0.3 }}
                  className="block text-[#F59E0B] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] font-bold tracking-tight mt-2 italic"
                >
                  {t('hero_title_accent')}
                </motion.span>
              </div>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: 0.45 }}
                className="text-lg text-slate-400 max-w-lg leading-relaxed"
              >
                {t('hero_subtitle')}
              </motion.p>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/quote">
                  <Button 
                    size="lg" 
                    className="btn-shine bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold px-8 h-14 text-base"
                    data-testid="hero-cta-quote"
                  >
                    {t('hero_cta')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-slate-600 text-white hover:bg-white/10 h-14 text-base"
                    data-testid="hero-cta-products"
                  >
                    {t('hero_cta_secondary')}
                  </Button>
                </Link>
              </motion.div>

              {/* Stats — glassmorphism */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: 0.75 }}
                className="flex gap-4 pt-4"
              >
                {[
                  { val: platformStats.products || 100, suffix: '+', label: t('stats_suppliers') === 'Verified Suppliers' ? 'Products' : 'Produtos', key: 'products' },
                  { val: platformStats.suppliers || 15, suffix: '+', label: t('stats_suppliers'), key: 'suppliers' },
                  { val: platformStats.countries || 2, suffix: '', label: t('stats_countries'), key: 'countries' },
                ].map((stat) => (
                  <div key={stat.key} className="glass-card rounded-xl px-5 py-3 min-w-[100px]">
                    <p className="text-2xl font-bold text-white">
                      <AnimatedCounter target={stat.val} suffix={stat.suffix} />
                    </p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Route visual */}
            <motion.div 
              initial={{ opacity: 0, x: 40 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hidden lg:flex flex-col items-center justify-center"
            >
              <div className="w-full max-w-md space-y-0">
                {/* Joburg card */}
                <div className="glass-card rounded-2xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#F59E0B]" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">Johannesburg</p>
                      <p className="text-slate-400 text-sm">South Africa</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-[#10B981] text-sm font-medium">Sourcing</p>
                      <p className="text-slate-500 text-xs">100+ suppliers</p>
                    </div>
                  </div>
                </div>
                
                {/* Animated route line */}
                <div className="flex flex-col items-center py-1">
                  <div className="route-line-container h-24">
                    <div className="route-light" />
                  </div>
                  <div className="glass-card rounded-full px-4 py-1.5 text-xs text-slate-300 font-medium my-1">
                    We handle everything
                  </div>
                  <div className="route-line-container h-24">
                    <div className="route-light" style={{ animationDelay: '1.5s' }} />
                  </div>
                </div>
                
                {/* Maputo card */}
                <div className="glass-card rounded-2xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#10B981]" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">Maputo</p>
                      <p className="text-slate-400 text-sm">Mozambique</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-[#F59E0B] text-sm font-medium">Delivered</p>
                      <p className="text-slate-500 text-xs">To your door</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CATEGORIES ===== */}
      <section className="section-padding bg-white" data-testid="categories-section">
        <div className="container-custom">
          <Reveal>
            <div className="mb-10">
              <h2 className="text-slate-900 mb-3">{t('categories_title')}</h2>
              <p className="text-slate-500 max-w-xl">{t('categories_subtitle')}</p>
            </div>
          </Reveal>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {Array(6).fill(0).map((_, i) => (
                <div key={`skeleton-${i}`} className="h-40 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <Reveal delay={0.1}>
              <CategoryGrid categories={categories} columns={6} showTitle={false} />
            </Reveal>
          )}

          <Reveal delay={0.2}>
            <div className="mt-10">
              <Link to="/products">
                <Button variant="outline" size="lg" className="border-slate-300 text-slate-700 hover:bg-slate-50">
                  {t('categories_view_all')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== TRENDING ===== */}
      {featuredProducts.length > 0 && (
        <section className="section-padding bg-slate-50" data-testid="trending-section">
          <div className="container-custom">
            <Reveal>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#F59E0B] mb-2 block">{t('trending')}</span>
                  <h2 className="text-2xl font-bold text-slate-900">{t('featured_products')}</h2>
                </div>
                <Link to="/products" className="text-slate-600 hover:text-[#1E3A8A] font-medium flex items-center gap-1 text-sm">
                  {t('view_all')}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {featuredProducts.slice(0, 8).map((product, index) => (
                <FeaturedProductCard key={product.product_id} product={product} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== HOW IT WORKS ===== */}
      <section className="section-padding bg-white" data-testid="how-it-works-section">
        <div className="container-custom">
          <Reveal>
            <div className="mb-12">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">{t('how_it_works_subtitle')}</span>
              <h2 className="text-slate-900">{t('how_it_works_title')}</h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step) => (
              <Reveal key={step.num} delay={steps.indexOf(step) * 0.1}>
                <div className="relative group">
                  {steps.indexOf(step) < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] border-t-2 border-dashed border-slate-200" />
                  )}
                  <div className="relative">
                    <span className="text-6xl font-black text-slate-100 absolute -top-4 -left-2 select-none group-hover:text-[#F59E0B]/20 transition-colors duration-500">
                      {step.num}
                    </span>
                    <div className="relative pt-8 pl-2">
                      <div className="w-12 h-12 rounded-xl bg-[#0F172A] flex items-center justify-center mb-4 group-hover:bg-[#1E3A8A] transition-colors duration-300">
                        <step.icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BRAND PILLARS ===== */}
      <section className="section-padding bg-[#0F172A] relative overflow-hidden" data-testid="value-props-section">
        <Particles />
        <div className="container-custom relative z-10">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <h2 className="text-white mb-4">{t('value_props_title')}</h2>
              <p className="text-slate-400 text-lg leading-relaxed">{t('value_props_subtitle')}</p>
            </div>
          </Reveal>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((pillar) => (
              <Reveal key={pillar.accent} delay={pillars.indexOf(pillar) * 0.1}>
                <div className="glass-card rounded-2xl p-6 h-full">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${pillar.accent}15` }}>
                    <pillar.icon className="w-5 h-5" style={{ color: pillar.accent }} />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-3">{pillar.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{pillar.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRUST QUOTE ===== */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-12 h-1 bg-[#F59E0B] mx-auto mb-8" />
              <blockquote className="text-xl sm:text-2xl text-slate-700 leading-relaxed font-medium italic">
                "{t('trust_quote')}"
              </blockquote>
              <div className="mt-6 text-sm text-slate-400 font-medium tracking-wide uppercase">
                The Guy in SA Team
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Social Feed */}
      <SocialFeed />

      {/* ===== CTA ===== */}
      <section className="section-padding bg-[#0F172A] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
        <div className="hero-grid-glow" />
        <div className="container-custom text-center relative z-10">
          <Reveal>
            <h2 className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">{t('cta_title')}</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto mb-10">{t('cta_subtitle')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register?role=buyer">
                <Button size="lg" className="btn-shine bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold px-10 h-14 text-base" data-testid="cta-buyer">
                  {t('cta_buyer')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/register?role=supplier">
                <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-white/10 h-14 text-base" data-testid="cta-supplier">
                  {t('cta_supplier')}
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex justify-center">
              <ShareButtons
                url="https://theguyinsa.co.za"
                title="The Guy in SA — Your guy on the other side"
                description="Cross-border procurement and logistics. Johannesburg to Maputo."
              />
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
